/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

// pcg-viewer canvas: visible-window Loop controller (handoff v0.8.3).
//
// The prototype implemented Loop as a set of top-level functions
// (visibleLoopBounds … activeLoopBounds, repeatLoopBoundaryWithFade,
// cancelLoopBoundaryTransition, followPlaybackIfNeeded, toggleVisibleWindowLoop,
// setIndependentLoopRange) over module globals (`state`, `view`, `D`,
// `loopBoundaryTransitioning`, `loopBoundaryTimer`, `loopBoundaryReleaseTimer`,
// `loopWheelCommitTimer`, `playbackMasterGain`), plus loop-specific branches
// spliced into the audio element listeners, the rAF tick and the transport
// buttons. Here all of that lives in one per-viewer controller:
//
//   - the module globals become closure variables of createLoopController
//   - `state`/`view`/`D` are rc.state / rc.view / rc.payload
//   - audio access, seeking, drawing, playback start, chrome re-sync and the
//     Web Audio master gain are injected `deps` (the React wrapper owns them)
//   - the listener/tick branches are exposed as handle*/playbackTick helpers
//     that the wrapper's existing listeners delegate to
//
// Function bodies are verbatim under the shim-prologue discipline; every
// upstream comment is kept. Upstream's drawWave();drawMini(); pairs map to the
// wrapper's redraw() (which also re-syncs chrome), and the single drawWave()
// inside setIndependentLoopRange maps to redraw() as well.
//
// Not implemented here (wrapper-owned, see CANVAS-MAP.md): the seekPlayback
// change `if(opts.keepVisible !== false && !state.windowLoop) ensurePlaybackVisible(next)`,
// startAudioPlayback (AudioContext resume + audio.play()), and the transport
// button chrome (transportIcon SVGs, aria-pressed, titles).

import { ensurePlaybackVisible as viewEnsurePlaybackVisible } from "./view"

// Upstream constant; retained for provenance (the immediate-restart strategy in
// repeatLoopBoundaryWithFade no longer schedules a fade, so it is unused there).
export const LOOP_BOUNDARY_FADE_S = 0.01

// Pure form of the prototype's activeLoopBounds() for the draw functions
// (drawWave reads it through rc.state so it needs no controller instance).
export function activeLoopBounds(state) {
  if (!state.windowLoop) return null
  // The loop range is independent of the displayed viewport. Panning and
  // zooming never rewrite it; only the ruler loop bar does.
  const bounds = state.loopRange
  if (!bounds || bounds.revision !== state.loopRevision || !(bounds.end > bounds.start)) return null
  return bounds
}

/**
 * createLoopController(rc, deps)
 *
 * deps:
 *   getAudio()                 -> HTMLAudioElement | null   (prototype audioPlayer())
 *   seekPlayback(t, opts)      -> boolean                   (wrapper's seekPlayback)
 *   redraw()                                               (wrapper's redraw: drawWave+drawMini+chrome)
 *   startAudioPlayback(audio)  -> Promise<boolean>          (resume suspended AudioContext, then audio.play())
 *   updateControls()           optional                    (prototype updateCompactAudioControls: re-sync
 *                                                           React transport/ruler chrome from rc.state)
 *   getMasterGain()            optional -> GainNode | null  (prototype playbackMasterGain; only read by
 *                                                           cancelLoopBoundaryTransition's gain reset)
 *   ensureAudioGraph()         optional                    (prototype ensurePlaybackFilter(): pre-create
 *                                                           the output graph when Loop turns on)
 */
export function createLoopController(rc, deps) {
  // ---- shim prologue (globals -> render context / injected deps) ----
  const D = rc.payload
  const { view, state } = rc
  const audioPlayer = () => deps.getAudio()
  const seekPlayback = (t, opts) => deps.seekPlayback(t, opts)
  const startAudioPlayback = (audio) => deps.startAudioPlayback(audio)
  const updateCompactAudioControls = () => {
    if (deps.updateControls) deps.updateControls()
  }
  const ensurePlaybackFilter = () => {
    if (deps.ensureAudioGraph) deps.ensureAudioGraph()
  }
  // Upstream always calls drawWave() then drawMini(); redraw() repaints both
  // canvases, so the trailing drawMini() becomes a no-op.
  const drawWave = () => deps.redraw()
  const drawMini = () => {}
  const ensurePlaybackVisible = (t) => viewEnsurePlaybackVisible(view, D.duration, t)
  // Prototype module globals, now per controller.
  let loopBoundaryTransitioning = false
  let loopBoundaryTimer = null
  let loopBoundaryReleaseTimer = null
  let loopWheelCommitTimer = null
  // ---- end shim prologue; bodies verbatim below ----

  function visibleLoopBounds() {
    // The waveform view is authoritative. Audio metadata can differ slightly
    // from the analyzed duration and must not silently shorten the visible loop.
    const displayDuration = Math.max(0, Number(D.duration) || 0)
    const start = Math.max(0, Math.min(Number(view.start) || 0, displayDuration))
    const visibleWidth = Math.max(0, Number(view.width) || 0)
    return { start, end: Math.max(start, Math.min(displayDuration, start + visibleWidth)) }
  }
  function captureVisibleLoopBounds() {
    return visibleLoopBounds()
  }
  function trackLoopToVisibleWindow() {
    if (!state.windowLoop) return null
    const bounds = captureVisibleLoopBounds()
    state.loopRevision += 1
    state.loopRange = { start: bounds.start, end: bounds.end, revision: state.loopRevision }
    return bounds
  }
  function syncLoopToVisibleWindow(opts = {}) {
    if (!state.windowLoop) return null
    cancelLoopBoundaryTransition()
    // Viewport navigation and loop-region editing are independent. Capture the
    // viewport only when Loop is first enabled (or explicitly reset).
    const bounds = opts.capture === true ? trackLoopToVisibleWindow() : activeLoopBoundsImpl()
    if (!bounds) return null
    const audio = audioPlayer()
    if (
      audio &&
      bounds.end > bounds.start &&
      (opts.restart === true || audio.currentTime < bounds.start || audio.currentTime >= bounds.end)
    ) {
      state.loopSeekPending = true
      state.loopSeekTarget = bounds.start
      if (!seekPlayback(bounds.start, { keepVisible: false })) state.loopSeekPending = false
    }
    return bounds
  }
  function beginLoopViewportEdit() {
    if (!state.windowLoop || state.loopViewportEditing) return
    // A commit scheduled by an earlier wheel burst is obsolete as soon as a
    // new pan/zoom gesture begins. Letting it fire during the new gesture can
    // install and restart an intermediate, shorter viewport.
    if (loopWheelCommitTimer) {
      clearTimeout(loopWheelCommitTimer)
      loopWheelCommitTimer = null
    }
    const audio = audioPlayer()
    state.loopViewportEditing = true
    state.loopEditResumePlayback = Boolean(audio && !audio.paused)
    cancelLoopBoundaryTransition()
    state.loopSeekPending = false
    state.loopSeekTarget = null
    if (audio && !audio.paused) audio.pause()
  }
  function finishLoopViewportEdit() {
    if (!state.windowLoop) {
      state.loopViewportEditing = false
      state.loopEditResumePlayback = false
      return
    }
    // change, pointerup and blur may all close the same browser gesture. Only
    // the first completion may resume playback.
    if (!state.loopViewportEditing) return
    const shouldResume = state.loopEditResumePlayback
    state.loopViewportEditing = false
    state.loopEditResumePlayback = false
    state.loopResumeAfterSeek = shouldResume
    // Zoom and pan alter only the viewport. The committed loop remains an
    // independent playback range until the user edits the ruler or toggles
    // Loop off and on. This preserves a 2-second loop when zooming out.
    syncLoopToVisibleWindow({ restart: false, capture: false })
    const audio = audioPlayer()
    // Do not resume against an intermediate/stale currentTime. The seeked
    // event below resumes only after the new authoritative range is installed.
    if (shouldResume && audio && !state.loopSeekPending) {
      state.loopResumeAfterSeek = false
      startAudioPlayback(audio)
    }
  }
  function activeLoopBoundsImpl() {
    return activeLoopBounds(state)
  }
  function repeatLoopBoundaryWithFade(bounds) {
    const audio = audioPlayer()
    if (!audio || !(bounds.end > bounds.start)) return false
    // Keep the visible viewport authoritative. A delayed gain/fade callback can
    // arrive after a rapid Chrome pan or zoom and reapply an obsolete range.
    // Interactive review therefore uses an immediate segment restart. The
    // source audio, calculated evidence and exports remain unchanged.
    const current = activeLoopBoundsImpl()
    if (!current) return false
    state.loopSeekPending = true
    state.loopSeekTarget = current.start
    if (!seekPlayback(current.start, { keepVisible: false })) {
      state.loopSeekPending = false
      state.loopSeekTarget = null
      return false
    }
    return true
  }
  function cancelLoopBoundaryTransition() {
    if (loopBoundaryTimer !== null) window.clearTimeout(loopBoundaryTimer)
    if (loopBoundaryReleaseTimer !== null) window.clearTimeout(loopBoundaryReleaseTimer)
    loopBoundaryTimer = null
    loopBoundaryReleaseTimer = null
    loopBoundaryTransitioning = false
    // Prototype: `if(audioCtx && playbackMasterGain)`; the gain node's own
    // context stands in for the audioCtx global.
    const playbackMasterGain = deps.getMasterGain ? deps.getMasterGain() : null
    if (playbackMasterGain && playbackMasterGain.context) {
      const now = playbackMasterGain.context.currentTime
      const gain = playbackMasterGain.gain
      gain.cancelScheduledValues(now)
      gain.setValueAtTime(1, now)
    }
  }
  function followPlaybackIfNeeded(t) {
    if (state.windowLoop || state.loopViewportEditing) return false
    return ensurePlaybackVisible(t)
  }

  // Transport: compactLoopToggle / waveLoopRuler click.
  async function toggleVisibleWindowLoop() {
    const audio = audioPlayer()
    if (!audio || !audio.src) return
    cancelLoopBoundaryTransition()
    const wasLooping = state.windowLoop
    if (wasLooping) state.loopSeekPending = false
    state.windowLoop = !state.windowLoop
    if (state.windowLoop) {
      // Build the playback-only output stage now so automatic loop boundaries
      // can use a short click-suppression fade even when filtering is Off.
      ensurePlaybackFilter()
      // syncLoopToVisibleWindow performs the single authoritative start seek.
      // A second assignment here can resolve out of order in Chrome after a
      // zoom gesture and begin playback part-way through the visible window.
      syncLoopToVisibleWindow({ restart: true, capture: true })
    } else if (wasLooping) {
      state.loopRevision += 1
      state.loopRange = null
      state.loopSeekTarget = null
    }
    updateCompactAudioControls()
    // Loop colour is a mode indicator, not a playback indicator. Redraw it
    // immediately when Loop is toggled, including while audio is stopped.
    drawWave()
    drawMini()
  }

  // Transport: the compactRestart branch that leaves Loop mode. The caller
  // then does seekPlayback(0, {keepVisible:true}); startAudioPlayback(audio).
  function disableLoop() {
    // Transport controls retain one meaning in every mode: Start means the
    // beginning of the recording. Loop is disabled because otherwise the
    // boundary controller would immediately return playback to the loop start.
    if (state.windowLoop) {
      state.windowLoop = false
      state.loopRevision += 1
      state.loopRange = null
      state.loopViewportEditing = false
      state.loopEditResumePlayback = false
      state.loopResumeAfterSeek = false
      state.loopSeekPending = false
      return true
    }
    return false
  }

  // Transport: the compactPlayToggle pause branch, before audio.pause().
  function prepareManualPause() {
    // Media controls never restore or rewrite a prior viewport. The
    // visible window changes only through an explicit pan or zoom.
    cancelLoopBoundaryTransition()
    state.loopSeekPending = false
  }

  // Ruler loop bar: install an explicit [start,end] range.
  function setIndependentLoopRange(start, end) {
    const minimum = Math.min(0.25, Math.max(0.05, D.duration))
    let a = Math.max(0, Math.min(Number(start) || 0, D.duration))
    let b = Math.max(0, Math.min(Number(end) || 0, D.duration))
    if (b - a < minimum) b = Math.min(D.duration, a + minimum)
    if (b - a < minimum) a = Math.max(0, b - minimum)
    state.loopRevision += 1
    state.loopRange = { start: a, end: b, revision: state.loopRevision }
    cancelLoopBoundaryTransition()
    state.loopSeekPending = false
    state.loopSeekTarget = null
    drawWave()
    updateCompactAudioControls()
    return state.loopRange
  }

  // syncAudio(): a new audio source drops any loop.
  function reset() {
    state.windowLoop = false
    state.loopRange = null
    state.loopRevision += 1
  }

  // ---- audio element listener branches (the wrapper's listeners delegate) ----

  // 'pause' listener prologue.
  function handlePause() {
    cancelLoopBoundaryTransition()
  }
  // 'ended' listener prologue. Returns true when the loop restarted playback
  // (the caller must then skip its stop/redraw tail, as upstream `return`s).
  function handleEnded() {
    const audio = audioPlayer()
    if (state.windowLoop && audio) {
      const bounds = activeLoopBoundsImpl()
      if (bounds) {
        seekPlayback(bounds.start)
        startAudioPlayback(audio)
        return true
      }
    }
    return false
  }
  // 'seeked' listener prologue; t is the clamped playback time (rc.getPlaybackTime()).
  // Returns false when an out-of-order seek was re-issued (upstream `return`s
  // before its chrome update/redraw), true when the caller should continue.
  function handleSeeked(t) {
    if (state.loopSeekPending) {
      const target = state.loopSeekTarget
      // Chrome may complete seeks out of order during several rapid swipes.
      // Only the seek for the latest committed viewport may resume playback.
      if (Number.isFinite(target) && Number.isFinite(t) && Math.abs(t - target) > 0.035) {
        seekPlayback(target, { keepVisible: false })
        return false
      }
      state.loopSeekTarget = null
      requestAnimationFrame(() => {
        state.loopSeekPending = false
      })
    }
    if (state.loopResumeAfterSeek) {
      state.loopResumeAfterSeek = false
      const audio = audioPlayer()
      if (audio) requestAnimationFrame(() => startAudioPlayback(audio))
    }
    if (!state.windowLoop) followPlaybackIfNeeded(t)
    return true
  }
  // 'timeupdate' listener prologue (replaces the unconditional ensurePlaybackVisible).
  function handleTimeUpdate(t) {
    // The animation-frame controller is the sole loop-boundary authority.
    // timeupdate is comparatively coarse and may arrive late or out of order
    // after Chrome seeks, so it must never initiate a competing loop restart.
    if (!state.windowLoop) {
      followPlaybackIfNeeded(t)
    }
  }
  // playbackRenderTick: the per-frame branch between the paused check and the draws.
  function playbackTick(audio) {
    if (state.windowLoop) {
      if (!state.loopViewportEditing && !state.loopSeekPending) {
        const bounds = activeLoopBoundsImpl()
        if (
          bounds &&
          bounds.end > bounds.start &&
          (audio.currentTime < bounds.start || audio.currentTime >= bounds.end)
        ) {
          repeatLoopBoundaryWithFade(bounds)
        }
      }
    } else {
      followPlaybackIfNeeded(audio.currentTime)
    }
  }

  // ---- viewport-commit timer (wheel 180 ms / zoom slider 120 ms) ----
  function cancelViewportCommit() {
    if (loopWheelCommitTimer) {
      clearTimeout(loopWheelCommitTimer)
      loopWheelCommitTimer = null
    }
  }
  // A trackpad/scroll-wheel gesture emits a burst of events. Commit exactly
  // one authoritative loop range after the burst settles, rather than
  // restarting playback against every intermediate viewport.
  function scheduleViewportCommit(ms) {
    if (loopWheelCommitTimer) clearTimeout(loopWheelCommitTimer)
    loopWheelCommitTimer = setTimeout(() => {
      loopWheelCommitTimer = null
      finishLoopViewportEdit()
    }, ms)
  }

  function dispose() {
    if (loopBoundaryTimer !== null) window.clearTimeout(loopBoundaryTimer)
    if (loopBoundaryReleaseTimer !== null) window.clearTimeout(loopBoundaryReleaseTimer)
    loopBoundaryTimer = null
    loopBoundaryReleaseTimer = null
    loopBoundaryTransitioning = false
    cancelViewportCommit()
  }

  return {
    visibleLoopBounds,
    trackLoopToVisibleWindow,
    syncLoopToVisibleWindow,
    beginLoopViewportEdit,
    finishLoopViewportEdit,
    activeLoopBounds: activeLoopBoundsImpl,
    repeatLoopBoundaryWithFade,
    cancelLoopBoundaryTransition,
    followPlaybackIfNeeded,
    toggleVisibleWindowLoop,
    disableLoop,
    prepareManualPause,
    setIndependentLoopRange,
    reset,
    handlePause,
    handleEnded,
    handleSeeked,
    handleTimeUpdate,
    playbackTick,
    cancelViewportCommit,
    scheduleViewportCommit,
    isBoundaryTransitioning: () => loopBoundaryTransitioning,
    dispose,
  }
}
